// Расход подписки по видам работ (разбор 26.09): какую долю окон лимита «5 часов» и «неделя» съела каждая работа
// агента и с какого порога она ждёт сброса окна. Самостоятельный блок: данные — /api/agent/usage.
import { Button, Card, EmptyState, Meter, pct, Skeleton, time, when } from '@autoaff/ui';
import { useQuery } from '@tanstack/react-query';
import { Gauge } from 'lucide-react';
import { api } from '../../api';

interface KindShare {
  kind: string;
  label: string;
  /** Доля окна, 0…1. */
  share: number;
  runs: number;
}

interface UsageResponse {
  windows: Array<{
    window: 'five_hour' | 'seven_day';
    utilization: number | null;
    resetsAt: string | null;
    byKind: KindShare[];
  }>;
  perRun: KindShare[];
  limits: Array<{ kind: string; label: string; fiveHour: number; sevenDay: number }>;
}

const WINDOW_RU = { five_hour: 'Окно 5 часов', seven_day: 'Неделя' } as const;

const works = (n: number) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return `${n} работа`;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return `${n} работы`;
  return `${n} работ`;
};

/** Пороги по строке на группу: виды с одинаковыми порогами — вместе. */
function limitLines(limits: UsageResponse['limits']): string[] {
  const groups = new Map<string, string[]>();
  for (const l of limits) {
    const key = `с ${pct(l.fiveHour * 100, 0)} окна 5 ч и ${pct(l.sevenDay * 100, 0)} недели`;
    groups.set(key, [...(groups.get(key) ?? []), l.label]);
  }
  return [...groups].map(
    ([k, labels]) => `${labels.map((x, i) => (i ? x.toLowerCase() : x)).join(', ')} — ${k}`,
  );
}

function WindowBlock({ w }: { w: UsageResponse['windows'][number] }) {
  const reset = w.resetsAt
    ? w.window === 'five_hour'
      ? `сброс в ${time(w.resetsAt)}`
      : `сброс ${when(w.resetsAt)}`
    : null;
  return (
    <section className="min-w-0">
      <h3 style={{ margin: '0 0 8px', fontSize: 'var(--fs-sm)', fontWeight: 600 }}>
        {WINDOW_RU[w.window]}
        {w.utilization !== null && (
          <span className="muted tnum" style={{ fontWeight: 400 }}>
            {' '}
            · занято {pct(w.utilization * 100, 0)}
            {reset ? ` · ${reset}` : ''}
          </span>
        )}
      </h3>
      {w.byKind.length === 0 ? (
        <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
          В этом окне работы агента пока ничего не потратили.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
          {w.byKind.map((k) => (
            <li
              key={k.kind}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto',
                gap: '4px 12px',
                alignItems: 'baseline',
              }}
            >
              <span className="min-w-0" style={{ fontSize: 'var(--fs-sm)' }}>
                {k.label} <span className="muted">· {works(k.runs)}</span>
              </span>
              <b className="tnum" style={{ fontSize: 'var(--fs-sm)' }}>
                {pct(k.share * 100)}
              </b>
              <div style={{ gridColumn: '1 / -1' }}>
                <Meter value={k.share} label={`${k.label}: ${pct(k.share * 100)} окна`} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function SubscriptionUsage() {
  const q = useQuery({
    queryKey: ['agent', 'usage'],
    queryFn: () => api<UsageResponse>('/api/agent/usage'),
    refetchInterval: 60_000,
  });
  return (
    <Card title="Расход подписки по видам работ">
      <p className="muted" style={{ margin: '0 0 12px', fontSize: 'var(--fs-sm)' }}>
        Какую долю окон лимита съела каждая работа агента. Это оценка: ваша работа в Claude Code вне платформы
        тратит те же окна.
      </p>
      {q.isPending && (
        <div style={{ display: 'grid', gap: 8 }}>
          <Skeleton w="60%" />
          <Skeleton w="90%" h={10} />
          <Skeleton w="70%" h={10} />
        </div>
      )}
      {q.isError && (
        <EmptyState
          icon={<Gauge />}
          title="Не удалось загрузить расход подписки"
          action={<Button onClick={() => q.refetch()}>Повторить</Button>}
        >
          {(q.error as Error).message}
        </EmptyState>
      )}
      {q.data && (
        <>
          {q.data.windows.every((w) => w.utilization === null) ? (
            <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
              Пока нет показаний окон: они появятся после первой работы агента или проверки доступа к
              подписке.
            </p>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: 'var(--gap)',
              }}
            >
              {q.data.windows.map((w) => (
                <WindowBlock key={w.window} w={w} />
              ))}
            </div>
          )}
          <dl className="facts" style={{ marginTop: 16 }}>
            <div>
              <dt>Ждут сброса окна</dt>
              <dd>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 2 }}>
                  {limitLines(q.data.limits).map((l) => (
                    <li key={l}>{l}</li>
                  ))}
                </ul>
                <small>
                  Обходы и тревоги идут почти до конца окна, долгая работа останавливается раньше.
                </small>
              </dd>
            </div>
            {q.data.perRun.length > 0 && (
              <div>
                <dt>За одну работу</dt>
                <dd className="tnum">
                  {q.data.perRun.map((k) => `${k.label} — ${pct(k.share * 100)}`).join(', ')}
                  <small>Доля недели на одну работу, в среднем за последние 7 дней.</small>
                </dd>
              </div>
            )}
          </dl>
        </>
      )}
    </Card>
  );
}
